import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Card, Spin } from 'antd';

import { isModelBusy } from '@libs/ModelAvailability';

import { useStateValue } from '@contexts/StateProvider';

import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';
import usePlaceholderImageFixed from '@hooks/Utils/UsePlaceholderImageFixed';

import ModelDetailsAge from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsAge';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';

import AddCompCardsButton from '@components/ActivePackages/AddCompCardsButton';
import AddFastPackageButton from '@components/ActivePackages/AddFastPackageButton';
import AddFavouriteBooksButton from '@components/ActivePackages/AddFavouriteBooksButton';
import { BookSelectorButton } from '@components/BookSelectorForPackages';
import ContactAlertPopover from '@components/ContactAlert/ContactAlertPopover';
import ModelDrawer from '@components/ContactDetails/ModelDetails/Drawer/ModelDrawer';
import AddToFolderButton from '@components/ContactsFolders/AddToFolderButton';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelAvailabilityIcon from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelAvailabilityIcon';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import MainFeatures from '@components/Features/MainFeatures';
import AvatarBg from '@components/Gui/AvatarBg';
import NwIcon from '@components/Gui/NwIcon';
import { NwTag } from '@components/Gui/NwTag.js';
import ModelInTown from '@components/ModelInTown/ModelInTown';

import { useShowSeasonFilterContext } from '@containers/Booking/ShowSeasons/ShowSeasonFilterContext';
import ShowSeasonModelWallInfo from '@containers/Booking/ShowSeasons/ShowSeasonModelWallInfo';

import { ContactContainer, InTownPanel, StyledCard, AgePanel } from './ModelCardUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import Title from 'antd/lib/typography/Title';

const selectionIconstyle = {
    position: 'absolute',
    left: '8px',
    top: '8px',
    fontSize: '24px',
    zIndex: 1,
};

const ModelCard = ({ model, highlightedFeatureIDs }) => {
    const navigate = useNavigate();
    const { additional, actions, cache, layout, selection, context } = useContactsSelector();
    const { disabledItems } = additional;
    const { selectedItemsIDs, multipleSelectionActive, itemClickAction } = actions;
    const { listType, activeSet, filters, quickFilters } = selection;
    const { modelAvailability } = cache;
    const { area } = context;

    const cs_dispatch = useContactsSelectorDispatch();
    const { seasonId: showSeasonFilterId } = useShowSeasonFilterContext();

    const showID = quickFilters && quickFilters.sort && quickFilters.sort === 'ID';

    // con un filtro sulle feature attivo le misure sono il dato in esame:
    // l'overlay resta aperto invece di comparire solo al passaggio del mouse
    const showFeaturesOverlay =
        !multipleSelectionActive &&
        Boolean(
            (listType === 'all' && quickFilters && quickFilters.showFeaturesQuickFilters) ||
                (listType === 'smart-lists' &&
                    filters &&
                    filters.active &&
                    filters.active.some((filter) => filter.source === 'feature'))
        );

    const disabled = useMemo(() => disabledItems.includes(model.ID), [disabledItems, model.ID]);
    const isSelected = useMemo(() => selectedItemsIDs.includes(model.ID), [selectedItemsIDs, model.ID]);

    const [loading, setLoading] = useState(false);
    const { Meta } = Card;
    const [{ areaContext }] = useStateValue();
    const { CG, ID, T, ST, G } = model;
    const [urlbg, isPlaceholder] = usePlaceholderImageFixed(CG, ST);

    const [isBusy, setIsBusy] = useState(false);
    const [isFetched, setFetched] = useState(false);
    const breakpoints = useNwBreakPoints();
    const [isOpenModelDrawer, setIsOpenModelDrawer] = useState(false);

    const onModelClick = () => {
        if (itemClickAction === 'navigate') {
            window.scrollTo(0, 0);
            if (listType && activeSet) {
                navigate(`/model/${model.ID}/${listType}/${activeSet}`);
            } else {
                navigate(`/model/${model.ID}`);
            }
        }
        if (itemClickAction === 'preview-drawer') {
            setIsOpenModelDrawer(true);
        }
    };

    const handleModelMultipleSelection = () => {
        cs_dispatch({ type: 'ToggleSelectedItem', ID: model.ID });
    };

    useEffect(() => {
        if (modelAvailability && modelAvailability.result) {
            const currentModelAvailability = modelAvailability.result
                ? modelAvailability.result.find((modelAv) => modelAv.ModelID === ID)
                : null;
            if (currentModelAvailability) {
                setIsBusy(true);
                setFetched(true);
            } else {
                if (modelAvailability && modelAvailability.result) {
                    setFetched(true);
                }
                setIsBusy(false);
            }
        }
    }, [modelAvailability]);

    const handleOpenModelDrawer = () => {
        setIsOpenModelDrawer(true);
    };

    const handleCloseModelDrawer = () => {
        setIsOpenModelDrawer(false);
    };

    const getCardActions = () => {
        const actions = [];
        if (!layout.drawer) {
            actions.push(<AddToFolderButton size="lg" ids={[ID]} type="model" showBadge />);
            actions.push(<BookSelectorButton modelID={ID} />);
            actions.push(<AddFastPackageButton modelID={ID} />);
            actions.push(<AddCompCardsButton modelID={ID} />);
            actions.push(<AddFavouriteBooksButton modelID={ID} />);
        }
        actions.push(<NwIcon size="lg" icon={byPrefixAndName.fasl['sidebar-flip']} onClick={handleOpenModelDrawer} />);
        return actions;
    };

    const ModelNameText = ({ arrayname }) => (
        <>
            {arrayname[0]}
            <br />
            {arrayname[1].trim() ? arrayname[1] : <br />}
        </>
    );

    const ModelNameCard = ({ model }) => {
        const arrayname = modelFullName(model, false, true);
        if (modelAvailability) {
            return (
                <div className="model-name-and-calendar">
                    <ModelAvailabilityIcon modelId={model.ID} isFetched={isFetched} isBusy={isBusy} />
                    <Title>
                        <ModelNameText arrayname={arrayname} />
                    </Title>
                </div>
            );
        }
        return (
            <Title>
                <ModelNameText arrayname={arrayname} />
            </Title>
        );
    };

    return (
        <Spin spinning={loading}>
            <StyledCard
                $status={isFetched ? (isBusy ? 'busy' : 'no-busy') : ''}
                hoverable
                className={multipleSelectionActive && isSelected ? 'selected' : null}
                cover={
                    <div className={`model-card-top${showFeaturesOverlay ? ' show-features' : ''}`}>
                        {isSelected && multipleSelectionActive && (
                            <NwIcon icon={byPrefixAndName.fass['check-circle']} style={selectionIconstyle} primary />
                        )}
                        <div
                            className={`model-cover${showSeasonFilterId ? ' has-season-info' : ''}`}
                            onClick={multipleSelectionActive ? handleModelMultipleSelection : onModelClick}
                        >
                            <AvatarBg
                                src={urlbg}
                                isPlaceholder={isPlaceholder}
                                height="18rem"
                                width="100%"
                                portrait
                                disabled={disabled}
                            />
                            <ShowSeasonModelWallInfo modelId={model.ID} variant="card" />
                            {!multipleSelectionActive && (
                                <div className="hover-container">
                                    <MainFeatures
                                        contact={model}
                                        showPublicValues
                                        highlightedFeatureIDs={highlightedFeatureIDs}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="tag-container">
                            {T.map((tag, index) => {
                                return (
                                    <NwTag type="dark" key={`${tag.ID}-${index}`}>
                                        {tag.Name}
                                    </NwTag>
                                );
                            })}
                        </div>
                        {showID && (
                            <div className="id-container">
                                <NwTag type="primary">{model.ID}</NwTag>
                            </div>
                        )}
                    </div>
                }
                actions={!multipleSelectionActive && getCardActions()}
            >
                <Meta
                    title={
                        <div className="model-name-container">
                            <ModelNameCard model={model} />
                            {model.AL && <ContactAlertPopover big contact={model} />}
                        </div>
                    }
                    description={
                        <>
                            <ModelDetailsHeaderAgencies
                                readOnly={multipleSelectionActive || layout.drawer}
                                model={model}
                            />
                            <ContactContainer>
                                <ContactUsers
                                    users={model.U}
                                    contactID={model.ID}
                                    readOnly={true}
                                    selected={isSelected}
                                />
                            </ContactContainer>
                            <InTownPanel
                                onClick={(event) => {
                                    if (!multipleSelectionActive && !layout.drawer) {
                                        event.stopPropagation();
                                    }
                                }}
                            >
                                <ModelInTown
                                    plainMode={multipleSelectionActive || layout.drawer}
                                    modelID={model.ID}
                                    modelInTownSchedule={model.ITS}
                                />
                            </InTownPanel>
                            {model.B && (
                                <AgePanel>
                                    <ModelDetailsAge birthday={model.B} small />
                                </AgePanel>
                            )}
                        </>
                    }
                    onClick={multipleSelectionActive ? handleModelMultipleSelection : onModelClick}
                />
                {
                    isOpenModelDrawer && (
                        // (area === 'accounting' ? (
                        //     <ModelDrawerAccounting modelID={model.ID} onClose={handleCloseModelDrawer} />
                        // ) : (
                        <ModelDrawer
                            modelID={model.ID}
                            hideCalendar={areaContext.area === 'scouting'}
                            showRating={areaContext.area === 'scouting'}
                            onClose={handleCloseModelDrawer}
                        />
                    )
                    //))
                }
            </StyledCard>
        </Spin>
    );
};

export default ModelCard;
